import React, { useState } from 'react';
import {
  Box,
  TableRow,
  TableCell,
  Chip,
  Button,
  IconButton,
  Typography,
  Stack,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  LocationOn as LocationIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
  Replay as ReplayIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Project } from '../../../models/Project';
import { DeploymentResult } from '../../../models/Deployment';
import { deriveServiceUrls } from '../utils/deriveServiceUrls';
import type { DeployTabType } from '../types';
import ServerStatusBadge from './ServerStatusBadge';
import OperationProgressBadge from './OperationProgressBadge';
import NextScheduleBadge from './NextScheduleBadge';
import usePermissions from '../../../hooks/usePermissions';

export type SiteTableRowMode = 'manage' | 'deploy';

export interface SiteRowData {
  site: Project;
  lastDeployment: DeploymentResult | null;
  isActive: boolean;
  isInProgress: boolean;
  /** site-batch-deploy: non-null when this site has a running batch. */
  activeBatchId?: number | null;
  /** 1-based index of the step currently dispatching (null when no active batch). */
  activeCurrentStep?: number | null;
  /** Total steps in the active batch (defaults to 8; null when no active batch). */
  activeTotalSteps?: number | null;
  /**
   * True when the site's most recent batch JUST finished (within the
   * "recently completed" window). Drives the transient "배포 완료" badge.
   */
  recentlyCompleted?: boolean;
}

interface SiteTableRowProps extends SiteRowData {
  /**
   * Row의 렌더링 모드.
   * - 'manage': /projects 페이지 — 단일 "배포" 버튼(클릭 시 /deploy 이동),
   *   중단/상세 버튼 없음, 진행률 텍스트/스피너 없음.
   * - 'deploy': /deploy 페이지 — 구동/재구동/중단/상세 버튼 풀 기능.
   *   기본값.
   */
  mode?: SiteTableRowMode;
  triggerDisabled: boolean;
  onTrigger: (site: Project) => void;
  onRowClick: (site: Project) => void;
  /** /deploy 모드에서 진행 모달을 여는 콜백. manage 모드에서는 무시됨. */
  onOpenBatch?: (site: Project, batchId: number) => void;
  /** /deploy 모드에서 사이트 전체 중단 콜백. manage 모드에서는 무시됨. */
  onStop?: (site: Project) => void;
  /** manage 모드 전용: 사이트 편집 아이콘 버튼 콜백. */
  onEdit?: (site: Project) => void;
  /** manage 모드 전용: 사이트 삭제 아이콘 버튼 콜백. */
  onDelete?: (site: Project) => void;
  /**
   * deploy-row-site-navigate: deploy 모드에서 "서비스 이동" 드롭다운 메뉴
   * 노출 조건. 'company' + dashboard_url 유효 시에만 버튼 렌더.
   */
  target?: DeployTabType;
  /**
   * dashboard-running-kpi-and-stop-relocate: parent registers a ref for each
   * row keyed by site.id so the KPI banner can scroll the row into view when
   * a chip is clicked. Optional — manage mode and tests can omit it.
   */
  rowRefs?: React.MutableRefObject<Map<string, HTMLElement>>;
}

// SiteTableView 의 모바일 카드 리스트에서도 동일 포맷을 쓰므로 export.
export const formatDateTime = (iso: string | undefined | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

type StatusLabel = '배포 중' | '배포 완료' | '구동 중' | '진행 중' | '실패' | '중지됨';

const resolveStatus = (
  row: SiteRowData,
): {
  label: StatusLabel;
  color: 'success' | 'info' | 'error' | 'default' | 'primary';
  progress?: string;
} => {
  if (row.activeBatchId && (row.activeCurrentStep ?? 0) > 0) {
    const step = row.activeCurrentStep ?? 0;
    const total = row.activeTotalSteps ?? 8;
    return { label: '배포 중', color: 'primary', progress: `${step}/${total}` };
  }
  if (row.recentlyCompleted) return { label: '배포 완료', color: 'success' };
  if (row.isInProgress) return { label: '진행 중', color: 'info' };
  if (row.isActive) return { label: '구동 중', color: 'success' };
  if (row.lastDeployment?.status === 'failed') return { label: '실패', color: 'error' };
  return { label: '중지됨', color: 'default' };
};

const SiteTableRow: React.FC<SiteTableRowProps> = ({
  site,
  lastDeployment,
  isActive,
  isInProgress,
  triggerDisabled,
  onTrigger,
  onRowClick,
  activeBatchId,
  activeCurrentStep,
  activeTotalSteps,
  recentlyCompleted,
  onOpenBatch,
  onStop,
  onEdit,
  onDelete,
  target,
  rowRefs,
  mode = 'deploy',
}) => {
  const theme = useTheme();
  const { canEdit, canAdmin } = usePermissions();
  const status = resolveStatus({
    site,
    lastDeployment,
    isActive,
    isInProgress,
    activeBatchId,
    activeCurrentStep,
    activeTotalSteps,
    recentlyCompleted,
  });

  const isManage = mode === 'manage';

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onTrigger(site);
  };

  const handleOpenBatchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeBatchId && onOpenBatch) onOpenBatch(site, activeBatchId);
  };

  const handleStopClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStop) onStop(site);
  };

  // deploy-row-site-navigate: "서비스 이동" 드롭다운 메뉴 상태.
  const [navAnchorEl, setNavAnchorEl] = useState<HTMLElement | null>(null);
  const navUrls = deriveServiceUrls(site.dashboard_url);
  const showNavigate = !isManage && target === 'company' && !!navUrls.admin;

  const openNavMenu = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setNavAnchorEl(e.currentTarget);
  };
  const closeNavMenu = (): void => setNavAnchorEl(null);
  const goTo =
    (url: string | null) =>
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
      closeNavMenu();
    };

  const hasActiveBatch = !!activeBatchId && (activeCurrentStep ?? 0) > 0 && !!onOpenBatch;
  const canStop = !isManage && (isActive || hasActiveBatch) && !!onStop;
  const showBatchButton = !isManage && hasActiveBatch;
  const showProgressCounter = !isManage && status.progress;
  const showProgressSpinner = !isManage && status.progress && status.label === '배포 중';
  const showLegacyInProgress = !isManage && isInProgress && !hasActiveBatch;

  // 버튼 라벨/아이콘: deploy 모드 상황별 "구동"/"재구동".
  // manage 모드는 배포 트리거 UI 미노출 (deploy-local-remote-subtab)
  const triggerLabel = isActive || hasActiveBatch ? '재구동' : '구동';
  const TriggerIcon = isActive || hasActiveBatch ? ReplayIcon : PlayIcon;

  const triggerTooltip = triggerDisabled
    ? '다른 현장 배포가 진행 중입니다'
    : hasActiveBatch
      ? '진행 중인 배치를 중단하고 다시 시작합니다'
      : isActive
        ? '이미 구동 중인 현장을 다시 배포합니다'
        : '';

  // dashboard-running-kpi-and-stop-relocate: register row element with the
  // parent's ref Map so RunningSitesKpi chip clicks can scroll into view.
  const handleRowMount = (el: HTMLTableRowElement | null): void => {
    if (!rowRefs) return;
    if (el) rowRefs.current.set(site.id, el);
    else rowRefs.current.delete(site.id);
  };

  return (
    <TableRow
      hover
      ref={handleRowMount}
      data-testid={`site-row-${site.id}`}
      onClick={() => onRowClick(site)}
      sx={{
        cursor: 'pointer',
        ...(status.label === '실패' && { bgcolor: alpha(theme.palette.error.main, 0.08) }),
        '&.row-highlight': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          transition: 'background-color 400ms ease',
        },
      }}
    >
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ wordBreak: 'keep-all', overflowWrap: 'anywhere', lineHeight: 1.6 }}
        >
          {site.name_ko?.trim() || site.name}
        </Typography>
        {site.name_ko?.trim() && (
          <Typography
            variant="caption"
            color="text.secondary"
            title={site.name}
            sx={{
              fontFamily: 'monospace',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              mt: 0.25,
            }}
          >
            {site.name}
          </Typography>
        )}
      </TableCell>
      {isManage && (
        <TableCell>
          <ServerStatusBadge siteId={site.id} />
        </TableCell>
      )}
      {isManage && (
        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 48,
              px: 1,
              py: 0.5,
              borderRadius: 1.5,
              fontSize: 13,
              fontWeight: (site.router_in_use_count ?? 0) > 0 ? 700 : 400,
              fontVariantNumeric: 'tabular-nums',
              color: (site.router_in_use_count ?? 0) > 0 ? 'primary.main' : 'text.secondary',
              bgcolor:
                (site.router_in_use_count ?? 0) > 0
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
            }}
          >
            {site.router_in_use_count ?? 0}대
          </Box>
        </TableCell>
      )}
      {!isManage && (
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <LocationIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            <Typography variant="body2" color="text.secondary">
              {site.location || '—'}
            </Typography>
          </Stack>
        </TableCell>
      )}
      <TableCell>
        {isManage ? (
          // 현장 관리: 구동(배포) 상태 대신 가장 가까운 일정 1건.
          <NextScheduleBadge siteId={site.id} />
        ) : (
          <Stack direction="row" spacing={0.75} alignItems="center">
            {showProgressSpinner && <CircularProgress size={14} color="primary" />}
            <Chip label={status.label} color={status.color} size="small" variant="outlined" />
            {showProgressCounter && (
              <Typography variant="caption" color="text.secondary">
                {status.progress}
              </Typography>
            )}
          </Stack>
        )}
      </TableCell>
      <TableCell>
        {/* site-operation-process-progress: 운영 프로세스 체크박스 진행률.
            React Query 로 사이트별 GET 호출 → 부모+자식 행 합산 비율 표시. */}
        <OperationProgressBadge siteId={site.id} />
      </TableCell>
      {!isManage && (
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(lastDeployment?.deployed_at)}
          </Typography>
        </TableCell>
      )}
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        {isManage ? (
          // manage 모드: 편집/삭제 아이콘 버튼 + chevron.
          // e.stopPropagation 로 row onClick(=navigateToMap) 전파 차단.
          <Stack direction="row" alignItems="center" spacing={0.25} justifyContent="flex-end">
            {/* 편집 = editor+ (사이트 정보 수정), 삭제 = admin only. backend RequireRole 매트릭스와 sync. */}
            {onEdit && canEdit && (
              <Tooltip title="편집">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(site);
                  }}
                  aria-label={`${site.name} 편집`}
                  data-testid={`site-edit-${site.id}`}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && canAdmin && (
              <Tooltip title="삭제">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(site);
                  }}
                  aria-label={`${site.name} 삭제`}
                  data-testid={`site-delete-${site.id}`}
                >
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            )}
            <ChevronRightIcon
              fontSize="small"
              sx={{ color: theme.palette.text.secondary }}
              data-testid={`site-row-chevron-${site.id}`}
            />
          </Stack>
        ) : (
          <>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              {showNavigate && (
                <Tooltip title="서비스 바로가기">
                  <IconButton
                    size="small"
                    onClick={openNavMenu}
                    aria-label={`${site.name} 서비스 이동 메뉴`}
                    data-testid={`site-navigate-${site.id}`}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {showBatchButton && (
                <Tooltip title={`배포 진행 #${activeBatchId} 상세 보기`}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleOpenBatchClick}
                    aria-label={`${site.name} 배포 상태 모달`}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={triggerTooltip}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<TriggerIcon />}
                    disabled={triggerDisabled}
                    onClick={handleTriggerClick}
                    aria-label={`${site.name} ${triggerLabel}`}
                    sx={{ fontWeight: 700, letterSpacing: 0.3, minWidth: 88 }}
                  >
                    {triggerLabel}
                  </Button>
                </span>
              </Tooltip>

              {/* dashboard-running-kpi-and-stop-relocate (v2): 위험 액션(중단)을
                  구동 버튼 오른쪽으로 분리하고 빨간색(error) Button + "중단"
                  라벨로 명확화. ml=1 (8px gap) 로 시각 분리. */}
              {canStop && (
                <Tooltip title="해당 현장의 모든 서비스를 중단합니다">
                  <Box component="span" sx={{ ml: 1, display: 'inline-flex' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={handleStopClick}
                      aria-label={`${site.name} 중단`}
                      data-testid={`site-row-stop-${site.id}`}
                      sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                    >
                      중단
                    </Button>
                  </Box>
                </Tooltip>
              )}
            </Stack>

            {showLegacyInProgress && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                justifyContent="flex-end"
                sx={{ mt: 0.5 }}
              >
                <CircularProgress size={10} />
                <Typography variant="caption" color="info.main">
                  legacy 배포 진행 중
                </Typography>
              </Stack>
            )}

            {showNavigate && (
              <Menu
                anchorEl={navAnchorEl}
                open={!!navAnchorEl}
                onClose={closeNavMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem
                  onClick={goTo(navUrls.admin)}
                  disabled={!navUrls.admin}
                  data-testid={`nav-admin-${site.id}`}
                >
                  <ListItemIcon>
                    <OpenInNewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Admin Client" secondary={navUrls.admin ?? '—'} />
                </MenuItem>
                <MenuItem
                  onClick={goTo(navUrls.routerSwagger)}
                  disabled={!navUrls.routerSwagger}
                  data-testid={`nav-router-${site.id}`}
                >
                  <ListItemIcon>
                    <OpenInNewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Router Swagger" secondary={navUrls.routerSwagger ?? '—'} />
                </MenuItem>
                <MenuItem
                  onClick={goTo(navUrls.lightsSwagger)}
                  disabled={!navUrls.lightsSwagger}
                  data-testid={`nav-lights-${site.id}`}
                >
                  <ListItemIcon>
                    <OpenInNewIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Lights Swagger" secondary={navUrls.lightsSwagger ?? '—'} />
                </MenuItem>
              </Menu>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
};

export default SiteTableRow;
